<template>
  <div>
    <b-container
      class="px-0"
      fluid
    >
      <b-row no-gutters>
        <b-col cols="12">
          <b-alert
            :show="errorSaving"
            class="mb-3"
            variant="danger"
          >
            An error occurred while attempting to save this catalogue, please try again.
          </b-alert>

          <b-alert
            :show="formUpdated"
            class="mb-3"
            variant="info"
          >
            There are currently unsaved changes for this catalogue.
          </b-alert>

          <div class="general-settings mb-5">
            <h4 class="mb-3">
              General Settings
            </h4>

            <b-form-group
              id="input-group-2"
              label="Catalogue Name:"
              label-for="input-2"
            >
              <b-form-input
                id="input-2"
                v-model="form.name"
                placeholder="Enter name"
                required
              />
            </b-form-group>

            <b-form-group
              id="input-group-2"
              label="Catalogue Description:"
              label-for="input-2"
            >
              <b-form-textarea
                id="input-2"
                v-model="form.description"
                placeholder="Product Catalogue description"
                required
              />
            </b-form-group>
            <b-form-group
              id="input-group-2"
              label="Published:"
              label-for="input-2"
            >
              <swoggle
                v-model="form.active"
                label-on="Yes"
                label-off="No"
              />
            </b-form-group>
          </div>

          <h4 class="mb-3">
            Product Settings
          </h4>

          <b-form-group
            id="input-group-3"
            label="Catalogue Products:"
            label-for="input-3"
            class="mb-5"
          >
            <typeahead-field-edit
              v-if="availableProducts.length > 0"
              :options="availableProducts"
              label="Add Product"
              value=""
              @input="addProduct"
            />
          </b-form-group>

          <b-table-simple v-if="form.products.length">
            <b-thead>
              <b-th>Order</b-th>
              <b-th>SKU</b-th>
              <b-th>Title</b-th>
              <b-th class="text-right">
                Actions
              </b-th>
            </b-thead>
            <draggable
              v-model="form.products"
              :class="{[`cursor-grabbing`]: dragging === true}"
              group="products"
              handle=".handle"
              tag="tbody"
              @end="dragging = false"
              @start="dragging = true"
            >
              <catalogue-product-data
                v-for="productID in form.products"
                :id="productID"
                :key="productID"
                @delete="deleteProduct"
              />
            </draggable>
          </b-table-simple>

          <b-button
            :disabled="submitNotAllowed || saving"
            class="mr-auto float-right"
            variant="primary"
            @click="onSubmit"
          >
            <template v-if="saving">
              <b-spinner
                small
                type="grow"
              />
              Saving...
            </template>
            <template v-else>
              Save Catalogue
            </template>
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import TypeaheadFieldEdit from './TypeaheadFieldEdit'
import Draggable from 'vuedraggable'
import CatalogueProductData from './CatalogueProductData.vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    TypeaheadFieldEdit,
    CatalogueProductData,
    Draggable
  },
  props: {
    catalogueid: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      saving: false,
      errorSaving: false,
      dragging: false,
      formWatcher: null,
      formUpdated: false,
      form: {
        uuid: '',
        name: '',
        description: '',
        products: [],
        active: true
      }
    }
  },
  computed: {
    ...mapGetters({
      products: 'catalogues/products'
    }),
    isEdit() {
      return this.catalogueid !== ''
    },
    submitNotAllowed() {
      return (this.form.name === '' || !this.form.products.length) || (this.isEdit && !this.formUpdated)
    },
    productOptions() {
      let products = this.products
      return products
      // return products.map(wine => ({
      //   text: wine.title,
      //   value: wine.id
      // }))
    },
    availableProducts() {
      const _this = this

      if (!this.form.products.length) {
        return this.productOptions
      }
      return this.productOptions.filter(option => {
        return !_this.form.products.some(id => id === option.id)
      })
    }
  },
  watch: {
    'catalogueid': {
      handler: function(val) {
        if (val !== '') {
          this.loadCatalogue()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.loading = true

    if (!this.isEdit) {
      this.form.uuid = this.createUUID()
    }
  },
  beforeDestroy() {
    if (this.formWatcher !== null) this.formWatcher()
  },
  methods: {
    deleteProduct(pid) {
      this.form.products = this.form.products.filter(id => id !== pid)
    },
    loadCatalogue() {
      this.$store.dispatch('blob/load', { blobID: 'product-catalogues--'.concat(this.catalogueid) })
        .then((results) => {
          if (JSON.stringify(results) === '{}') {
            this.$store.commit('SET_PAGE_SETTING', {
              key: 'error',
              value: 404
            })
          }
          else {
            this.$set(
              this,
              'form',
              Object.prototype.hasOwnProperty.call(results, 'data')
                ? results.data
                : results
            )

            this.$nextTick(() => {
              this.formWatcher = this.$watch('form', function() {
                this.formUpdated = true
              }, { deep: true })
            })
          }
        })
        .catch((error) => {
          console.error(error.message)
          this.$router.push('/catalogues/view')
        })
        .finally(() => (this.loading = false))
    },
    createUUID() {
      return uuidv4().toString()
    },
    addProduct(id) {
      this.form.products.push(id)
    },
    onSubmit: function() {
      event.preventDefault()
      this.saving = true
      const formData = this.form
      const prefix = 'product-catalogues--'
      const blobName = prefix.concat(formData.uuid)
      const blobID = encodeURIComponent(blobName)

      return this.$store.dispatch('blob/save', {
        blobID: blobID,
        blobData: formData
      })
        .then((response) => {
          this.$router.push('/catalogues/view')
        })
        .catch((error) => {
          console.error(error.message)
          this.errorSaving = true
        })
        .finally(() => {
          this.saving = false
        })
    },
    onReset(event) {
      event.preventDefault()

      // Reset our form values
      this.form.name = ''
      this.form.products = ''
    }
  }
}
</script>

<style
    lang="scss"
    scoped>
.handle {
  cursor: pointer;
}

.move-icon {
  width: 30px;
}
</style>
